import React, { useRef } from 'react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { Dialog, DialogContent } from '../../../ui/dialog';
import { Button } from '../../../ui/button';

export type ImageCropDialogProps = {
  title?: string;
  open: boolean;
  imageSrc: string | undefined;
  uploadedFile: File | undefined;
  onCancel: () => void;
  onConfirm: (croppedBlob: Blob | undefined) => void;
};

const ImageCropDialog = (props: ImageCropDialogProps) => {
  const { title = 'Crop image', open, imageSrc, onCancel, onConfirm, uploadedFile } = props;

  const cropperRef = useRef<any>(null);

  // Cancel crop dialog
  const handleCancel = () => {
    onCancel();
  };
  // Confirm crop dialog
  const handleConfirm = () => {
    const cropper = cropperRef?.current?.cropper
    if (!cropper) {
     onConfirm(undefined) 
     return
    }

    const canvas = cropper.getCroppedCanvas({
      fillColor: '#ffffff',
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    })

    if (!canvas) {
      onConfirm(undefined)
      return
    }

    const outputType =
      uploadedFile?.type?.includes('svg') || uploadedFile?.type?.includes('webp')
        ? 'image/png'
        : uploadedFile?.type || 'image/jpeg';

    canvas.toBlob(
      (blob: Blob | null) => {
        if (!blob) {
          onConfirm(undefined)
          return
        }

        onConfirm(blob)
      },
      outputType,
      0.9
    )

    // const croppedDataUrl = canvas.toDataURL('image/png')
    // onConfirm(croppedDataUrl);
  };
  // Full image cropbox
  const handleFullCropBox = () => {
    const cropper = cropperRef?.current?.cropper
    if (!cropper) return

    cropper.reset()

    const container = cropper.getContainerData()

    cropper.setCropBoxData({
      top: 0,
      left: 0,
      width: container.width,
      height: container.height,
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          handleCancel();
        }
      }}
    >
      <DialogContent className="p-6 max-w-3xl w-full">
        <div id="body" className="flex flex-col gap-4">
          <div className="flex flex-col space-y-1.5 text-center sm:text-left">
            <h2 className="text-lg font-semibold leading-none tracking-tight">{title}</h2>
          </div>

          <p className="m-0 text-sm text-muted-foreground">
            Drag the frame to position it, or drag a corner to resize. The frame matches the aspect
            ratio printed on the work order. Use{' '}
            <span className="font-medium">Fit whole image</span> to include the entire image — empty
            space will be filled with white padding.
          </p>

          <div id="cropper">
            <Cropper
              ref={cropperRef}
              src={imageSrc}
              className="w-full [@media(max-height:600px)]:max-h-[250px] [@media(min-height:600px)]:max-h-[420px] h-full"
              viewMode={1}
              dragMode="move"
              autoCropArea={1}
              guides
              center
              responsive
              restore={false}
              checkCrossOrigin={false}
              checkOrientation
              background={false}
              highlight={false}
              zoomable
              rotatable
              scalable
              movable
              cropBoxMovable
              cropBoxResizable
              toggleDragModeOnDblclick={false}
            />
          </div>

          <div id="footer-wrapper" className="flex justify-between items-center gap-1">
            <div>
              <Button onClick={handleFullCropBox} type="button" variant={'outline'}>
                Fit whole image (pad sides)
              </Button>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleCancel} type="button" variant={'outline'}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} type="button">
                Use this crop
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropDialog;
